import React, { useMemo, useState, useRef } from 'react';
import {
  View,
  StyleSheet,
  Modal,
  ScrollView,
  Platform,
} from 'react-native';
import { Text, TextInput } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { IColumnConfig, IFilterModel, ISortModel } from '@/types/admin/dataTable';
import { SVG_COMPONENTS } from '@/assets/admin';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { DesktopFilterRow } from './DesktopFilterRow';
import { useDataTableState } from '@/contexts/admin/DataTableContext';

interface DesktopTableFiltersProps<T> {
  columns: IColumnConfig<T>[];
  searchQuery: string;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
  showSearch?: boolean;
  showSort?: boolean;
  showFilter?: boolean;
  filterModel: IFilterModel;
  onFilterChange: (filterModel: IFilterModel) => void;
  sortModel: ISortModel[];
  onSortChange: (sortModel: ISortModel[]) => void;
  onExport?: () => void;
  showExport?: boolean;
  headerRight?: React.ReactNode;
}

export const DesktopTableFilters = <T,>({
  columns,
  searchQuery,
  onSearchChange,
  searchPlaceholder = 'Search...',
  showSearch = true,
  showSort = true,
  showFilter = true,
  filterModel,
  onFilterChange,
  sortModel,
  onSortChange,
  onExport,
  showExport = false,
  headerRight,
}: DesktopTableFiltersProps<T>) => {
  const styles = useStyles();
  const { admin, redesign } = useAdminColors();
  const { tableKey } = useDataTableState();
  const isFundingTable = tableKey === 'admin:funding';
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [openPicker, setOpenPicker] = useState<null | { row: number; kind: 'column' | 'operator' | 'value' }>(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortAnchor, setSortAnchor] = useState<AnchorRect | null>(null);
  const sortBtnRef = useRef<View | null>(null);
  const sortBtnTestId = useMemo(() => `desktop-table-sort-btn-${Math.random().toString(36).slice(2, 8)}`, []);

  const filterableColumns = columns.filter((col) => col.filterable);
  const sortableColumns = columns.filter((col) => col.sortable);

  const activeFiltersCount = filterModel.items?.length || 0;
  const activeSortCount = sortModel.length;

  const columnByKey = useMemo(() => {
    const map: Record<string, IColumnConfig<T>> = {} as any;
    filterableColumns.forEach((c) => {
      map[c.key as string] = c;
    });
    return map;
  }, [filterableColumns]);

  const openSortDropdown = () => {
    try {
      if (Platform.OS === 'web') {
        const el: any = (globalThis as any)?.document?.querySelector?.(`[data-testid="${sortBtnTestId}"]`);
        if (el && el.getBoundingClientRect) {
          const r = el.getBoundingClientRect();
          setSortAnchor({ x: r.left, y: r.top, w: r.width, h: r.height });
          setShowSortDropdown(true);
          return;
        }
      }

      if (sortBtnRef.current) {
        const RN: any = require('react-native');
        const node = RN.findNodeHandle(sortBtnRef.current);
        if (node && RN.UIManager && RN.UIManager.measureInWindow) {
          RN.UIManager.measureInWindow(node, (x: number, y: number, width: number, height: number) => {
            setSortAnchor({ x, y, w: width, h: height });
            setShowSortDropdown(true);
          });
          return;
        }
      }

      setShowSortDropdown(true);
    } catch {
      setShowSortDropdown(true);
    }
  };

  const getOperatorsForColumn = (col?: IColumnConfig<T>): Array<{ label: string; value: string }> => {
    if (isFundingTable) {
      return [{ label: 'is', value: 'is' }];
    }
    if (!col) return [
      { label: 'contains', value: 'contains' },
      { label: 'is', value: 'is' },
      { label: 'is not', value: 'isNot' },
    ];
    switch (col.filterType) {
      case 'select':
      case 'multiSelect':
        return [
          { label: 'is', value: 'is' },
          { label: 'is not', value: 'isNot' },
        ];
      case 'date':
        return [
          { label: 'on or after', value: 'onOrAfter' },
          { label: 'on or before', value: 'onOrBefore' },
        ];
      case 'boolean':
        return [{ label: 'is', value: 'is' }];
      case 'text':
      default:
        return [
          { label: 'contains', value: 'contains' },
          { label: 'is', value: 'is' },
          { label: 'is not', value: 'isNot' },
        ];
    }
  };

  const handleAddFilter = () => {
    const newFilter = {
      field: filterableColumns[0]?.key as string,
      operator: isFundingTable ? 'is' : 'contains',
      value: '',
    };
    onFilterChange({ ...filterModel, items: [...(filterModel.items || []), newFilter] });
  };

  const handleRemoveFilter = (index: number) => {
    const newItems = [...(filterModel.items || [])];
    newItems.splice(index, 1);
    onFilterChange({ ...filterModel, items: newItems });
  };

  const handleFilterChange = (index: number, field: string, value: any) => {
    const newItems = [...(filterModel.items || [])];
    newItems[index] = { ...newItems[index], [field]: value };
    onFilterChange({ ...filterModel, items: newItems });
  };

  const handleChangeColumn = (index: number, nextKey: string) => {
    const newItems = [...(filterModel.items || [])];
    const col = columnByKey[nextKey];
    const operators = getOperatorsForColumn(col);
    newItems[index] = { field: nextKey, operator: operators[0].value, value: '' };
    onFilterChange({ ...filterModel, items: newItems });
  };

  const handleChangeOperator = (index: number, op: string) => {
    handleFilterChange(index, 'operator', op);
  };

  const renderFilterModal = () => (
    <Modal
      visible={showFilterModal}
      transparent
      animationType="fade"
      onRequestClose={() => setShowFilterModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Filters</Text>
            <Hoverable hover="row" onPress={() => setShowFilterModal(false)} style={styles.closeButton}>
              <SVG_COMPONENTS.X_CIRCLE width={20} height={20} color={admin.dataTable.text} />
            </Hoverable>
          </View>

          <View style={styles.modalBody}>
            {filterModel.items?.map((filter, index) => {
              const currentColumn = columnByKey[filter.field];
              const operatorOptions = getOperatorsForColumn(currentColumn);
              return (
                <View
                  key={index}
                  style={[
                    styles.filterRow,
                    { width: '100%' },
                    openPicker?.row === index ? styles.filterRowActive : undefined,
                  ]}
                >
                  <DesktopFilterRow
                    index={index}
                    filter={filter}
                    columnByKey={columnByKey}
                    filterableColumns={filterableColumns}
                    operatorOptions={operatorOptions}
                    onChangeField={(i, key) => handleChangeColumn(i, key)}
                    onChangeOperator={(i, op) => handleChangeOperator(i, op)}
                    onChangeValue={(i, v) => handleFilterChange(i, 'value', v)}
                    onRemove={(i) => handleRemoveFilter(i)}
                    onOpenColumnPicker={(i) =>
                      setOpenPicker((prev) => (prev && prev.row === i && prev.kind === 'column' ? null : { row: i, kind: 'column' }))
                    }
                    onOpenOperatorPicker={(i) =>
                      setOpenPicker((prev) => (prev && prev.row === i && prev.kind === 'operator' ? null : { row: i, kind: 'operator' }))
                    }
                    onOpenValuePicker={(i) =>
                      setOpenPicker((prev) => (prev && prev.row === i && prev.kind === 'value' ? null : { row: i, kind: 'value' }))
                    }
                  />
                  {openPicker?.row === index && openPicker.kind === 'column' && (
                    <View style={styles.pickerPanel}>
                      <ScrollView style={styles.pickerScrollView} nestedScrollEnabled>
                        {filterableColumns.map((col) => (
                          <Hoverable hover="row"
                            key={col.key as string}
                            style={styles.pickerOption}
                            onPress={() => {
                              handleChangeColumn(index, col.key as string);
                              setOpenPicker(null);
                            }}
                          >
                            <Text style={styles.pickerOptionText}>{col.label}</Text>
                          </Hoverable>
                        ))}
                      </ScrollView>
                    </View>
                  )}
                  {openPicker?.row === index && openPicker.kind === 'operator' && (
                    <View style={styles.pickerPanel}>
                      <ScrollView style={styles.pickerScrollView} nestedScrollEnabled>
                        {operatorOptions.map((op) => (
                          <Hoverable hover="row"
                            key={op.value}
                            style={styles.pickerOption}
                            onPress={() => {
                              handleChangeOperator(index, op.value);
                              setOpenPicker(null);
                            }}
                          >
                            <Text style={styles.pickerOptionText}>{op.label}</Text>
                          </Hoverable>
                        ))}
                      </ScrollView>
                    </View>
                  )}
                  {openPicker?.row === index && openPicker.kind === 'value' && (
                    <View style={styles.pickerPanel}>
                      <ScrollView style={styles.pickerScrollView} nestedScrollEnabled>
                        {(currentColumn?.filterOptions || []).map((opt) => (
                          <Hoverable hover="row"
                            key={`${opt.label}-${opt.value}`}
                            style={styles.pickerOption}
                            onPress={() => {
                              handleFilterChange(index, 'value', opt.value);
                              setOpenPicker(null);
                            }}
                          >
                            <Text style={styles.pickerOptionText}>{opt.label}</Text>
                          </Hoverable>
                        ))}
                      </ScrollView>
                    </View>
                  )}
                </View>
              );
            })}

            <Hoverable hover="link" onPress={handleAddFilter} style={styles.addButton}>
              {({ textStyle }) => (
                <>
                  <SVG_COMPONENTS.ADD_BUTTON width={16} height={16} color={admin.formInput.borderFocused} />
                  <Text style={[styles.addButtonText, { color: admin.formInput.borderFocused }, textStyle]}>
                    Add Filter
                  </Text>
                </>
              )}
            </Hoverable>
          </View>

          {openPicker && (
            <Hoverable
              hover="none"
              pressedOpacity={1}
              style={styles.dropdownDismissOverlay}
              onPress={() => setOpenPicker(null)}
            />
          )}

          <View style={styles.modalFooter}>
            <Hoverable hover="glass"
              onPress={() => {
                onFilterChange({ items: [] });
                setShowFilterModal(false);
              }}
              style={styles.secondaryButton}
            >
              <Text style={styles.secondaryButtonText}>Clear All</Text>
            </Hoverable>

            <Hoverable
              hover="button"
              testID="filters-apply"
              onPress={() => setShowFilterModal(false)}
              style={styles.primaryButton}
            >
              <Text style={styles.primaryButtonText}>Apply</Text>
            </Hoverable>
          </View>
        </View>
      </View>
    </Modal>
  );

  return (
    <View style={styles.container}>
      <View style={styles.leftSection}>
        {showSearch && (
          <View style={styles.searchContainer}>
            <SVG_COMPONENTS.MAGNIFYING_GLASS width={16} height={16} color={redesign.text.dim} />
            <TextInput
              style={[styles.searchInput, Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : null]}
              value={searchQuery}
              onChangeText={onSearchChange}
              placeholder={searchPlaceholder}
              placeholderTextColor={admin.formInput.placeholder}
            />
          </View>
        )}
      </View>

      <View style={styles.rightSection}>
        {headerRight}
        {showFilter && (
          <Hoverable hover={activeFiltersCount > 0 ? 'none' : 'glass'}
            testID="filters-toggle"
            onPress={() => setShowFilterModal(true)}
            style={[styles.filterButton, activeFiltersCount > 0 && styles.filterButtonActive]}
          >
            <SVG_COMPONENTS.FILTER
              width={16}
              height={16}
              color={activeFiltersCount > 0 ? admin.formInput.borderFocused : redesign.text.dim}
            />
            <Text style={[styles.filterButtonText, activeFiltersCount > 0 && styles.filterButtonTextActive]}>
              Filter
            </Text>
            {activeFiltersCount > 0 && (
              <View style={styles.badge}>
                <Text style={styles.badgeText}>{activeFiltersCount}</Text>
              </View>
            )}
          </Hoverable>
        )}

        {showSort && (
          <Hoverable hover={activeSortCount > 0 ? 'none' : 'glass'}
            ref={sortBtnRef as any}
            testID={sortBtnTestId}
            onPress={openSortDropdown}
            style={[styles.filterButton, activeSortCount > 0 && styles.filterButtonActive]}
          >
            <SVG_COMPONENTS.CHEVRON_DOWN
              width={16}
              height={16}
              color={activeSortCount > 0 ? admin.formInput.borderFocused : redesign.text.dim}
            />
            <Text style={[styles.filterButtonText, activeSortCount > 0 && styles.filterButtonTextActive]}>
              Sort
            </Text>
            {activeSortCount > 0 && (
              <View style={styles.badge}>
                <Text style={styles.badgeText}>{activeSortCount}</Text>
              </View>
            )}
          </Hoverable>
        )}

        {showExport && (
          <Hoverable hover="glass" onPress={onExport} style={styles.secondaryButton}>
            <SVG_COMPONENTS.DOWNLOAD width={16} height={16} color={admin.dataTable.text} />
            <Text style={styles.secondaryButtonText}>Export</Text>
          </Hoverable>
        )}
      </View>

      {renderFilterModal()}

      <AnchoredDropdown
        isOpen={showSortDropdown}
        anchor={sortAnchor}
        width={240}
        maxHeight={320}
        onRequestClose={() => setShowSortDropdown(false)}
      >
        <View style={styles.sortDropdownContainer}>
          {sortableColumns.map((col) => {
            const isActive = sortModel[0]?.field === (col.key as any);
            const defaultSort = (col as any).defaultSort || 'asc';
            return (
              <Hoverable hover="row"
                key={`${String(col.key)}-single`}
                style={styles.sortDropdownItem}
                onPress={() => {
                  onSortChange([{ field: col.key as any, sort: defaultSort }]);
                  setShowSortDropdown(false);
                }}
              >
                <Text style={[styles.sortDropdownText, isActive && styles.sortActive]}>{col.label}</Text>
              </Hoverable>
            );
          })}
        </View>
      </AnchoredDropdown>
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors, semantic }) => {
    const admin = components.admin;
    return StyleSheet.create({
      container: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 12,
        backgroundColor: admin.dataTable.headerBg,
        borderBottomWidth: 1,
        borderBottomColor: admin.dataTable.border,
      },
      leftSection: {
        flex: 1,
        marginRight: 16,
      },
      rightSection: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
      },
      searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: admin.formInput.bg,
        borderWidth: 1,
        borderColor: admin.formInput.border,
        borderRadius: 6,
        paddingHorizontal: 12,
        paddingVertical: 8,
        maxWidth: 300,
      },
      searchInput: {
        flex: 1,
        marginLeft: 8,
        fontSize: 14,
        color: admin.formInput.text,
      },
      filterButton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: admin.dataTable.border,
        backgroundColor: admin.dataTable.filterBg,
        gap: 6,
      },
      filterButtonActive: {
        borderColor: admin.formInput.borderFocused,
        backgroundColor: admin.selector.itemSelectedBg,
      },
      filterButtonText: {
        fontSize: 14,
        fontWeight: '500',
        color: colors.text.dim,
      },
      filterButtonTextActive: {
        color: admin.dataTable.text,
      },
      badge: {
        backgroundColor: admin.formInput.borderFocused,
        borderRadius: 10,
        minWidth: 18,
        height: 18,
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 4,
      },
      badgeText: {
        color: '#FFFFFF',
        fontSize: 11,
        fontWeight: '600',
      },
      primaryButton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 6,
        gap: 6,
        backgroundColor: admin.formInput.borderFocused,
      },
      primaryButtonText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '500',
      },
      secondaryButton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 6,
        gap: 6,
        borderWidth: 1,
        borderColor: admin.dataTable.border,
        backgroundColor: 'transparent',
      },
      secondaryButtonText: {
        color: admin.dataTable.text,
        fontSize: 14,
        fontWeight: '500',
      },

      // Modal styles
      modalOverlay: {
        flex: 1,
        backgroundColor: semantic.background.overlay,
        justifyContent: 'center',
        alignItems: 'center',
      },
      modalContent: {
        backgroundColor: admin.selector.dropdownBg,
        borderWidth: 1,
        borderColor: admin.dataTable.border,
        borderRadius: 8,
        width: '90%',
        maxWidth: 600,
        maxHeight: '80%',
        overflow: 'visible',
        position: 'relative',
      },
      modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: admin.dataTable.border,
      },
      modalTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: admin.dataTable.text,
      },
      closeButton: {
        padding: 4,
      },
      modalBody: {
        padding: 16,
        maxHeight: 400,
        overflow: 'visible',
        position: 'relative',
        zIndex: 2,
      },
      filterRow: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 12,
        marginBottom: 12,
        position: 'relative',
      },
      filterRowActive: {
        zIndex: 5,
      },
      pickerPanel: {
        position: 'absolute',
        top: 64,
        left: 0,
        right: 0,
        backgroundColor: admin.selector.dropdownBg,
        borderWidth: 1,
        borderColor: admin.selector.border,
        borderRadius: 6,
        zIndex: 1000,
        maxHeight: 300,
        overflow: 'hidden',
      },
      pickerScrollView: {
        maxHeight: 300,
      },
      pickerOption: {
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: admin.dataTable.border,
      },
      pickerOptionText: {
        color: admin.dataTable.text,
        fontSize: 14,
      },
      addButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 8,
        marginTop: 8,
        gap: 6,
      },
      addButtonText: {
        fontSize: 14,
        fontWeight: '500',
      },
      modalFooter: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 8,
        padding: 16,
        borderTopWidth: 1,
        borderTopColor: admin.dataTable.border,
      },
      dropdownDismissOverlay: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1,
      },
      sortDropdownContainer: {
        backgroundColor: admin.selector.dropdownBg,
        borderWidth: 1,
        borderColor: admin.selector.border,
        borderRadius: 8,
        overflow: 'hidden',
      },
      sortDropdownItem: {
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: admin.dataTable.border,
      },
      sortDropdownText: {
        color: admin.dataTable.text,
        fontSize: 14,
      },
      sortActive: {
        fontWeight: '600',
        textDecorationLine: 'underline',
        color: admin.formInput.borderFocused,
      },
    });
  });
